import type { CSSProperties, Dispatch, SetStateAction } from 'react';
import { Link } from 'react-router-dom';
import { CONTRACT_ADDRESSES } from '../config/contracts';
import {
  PUBLIC_DESIGN_MODES,
  getButtonSurfaceStyle,
  type DesignMode,
} from '../config/theme';
import { ThemeSelector } from './ThemeSelector';
import { useTheme } from '../context/ThemeContext';

type Theme = ReturnType<typeof useTheme>;

function formatEthPriceTimestamp(ts: number): string {
  return (
    new Date(ts).toLocaleTimeString(undefined, {
      timeZone: 'UTC',
      hour: '2-digit',
      minute: '2-digit',
    }) + ' (UTC)'
  );
}

type FooterActionsProps = {
  theme: Theme;
  designMode: DesignMode;
  setDesignMode: (m: DesignMode) => void;
  hoverMode: number | null;
  setHoverMode: Dispatch<SetStateAction<number | null>>;
  fetchedAt: number | null | undefined;
  priceError: unknown;
  onLegalClick: () => void;
  onItemActivate?: () => void;
  layout: 'inline' | 'stack';
  themeModes?: DesignMode[];
};

export function FooterActions({
  theme,
  designMode,
  setDesignMode,
  hoverMode,
  setHoverMode,
  fetchedAt,
  priceError,
  onLegalClick,
  onItemActivate,
  layout,
  themeModes = PUBLIC_DESIGN_MODES,
}: FooterActionsProps) {
  const isStack = layout === 'stack';
  const textColor = theme.footerTextColor ?? theme.textMuted;

  const stackItemStyle: CSSProperties = {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '0.35rem 0.75rem',
    fontSize: '0.6875rem',
    fontWeight: 300,
    letterSpacing: '0.02em',
    color: theme.textMuted ?? '#9ca3af',
    textDecoration: 'none',
    ...getButtonSurfaceStyle(theme),
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  };

  const inlineLinkStyle: CSSProperties = {
    color: textColor,
    textDecoration: 'none',
  };

  const inlineButtonStyle: CSSProperties = {
    color: textColor,
    textDecoration: 'none',
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    cursor: 'pointer',
  };

  const handleActivate = () => onItemActivate?.();

  const copyright = (
    <span style={isStack ? { ...stackItemStyle, cursor: 'default' } : undefined}>
      © {new Date().getFullYear()} takni.xyz
    </span>
  );

  const documents = (
    <Link
      to="/docs"
      onClick={handleActivate}
      style={isStack ? stackItemStyle : inlineLinkStyle}
    >
      Documents
    </Link>
  );

  const legal = (
    <button
      type="button"
      onClick={() => {
        handleActivate();
        onLegalClick();
      }}
      style={isStack ? stackItemStyle : inlineButtonStyle}
    >
      Legal
    </button>
  );

  const baseScan = (
    <a
      href={`https://basescan.org/address/${CONTRACT_ADDRESSES.AAFactory}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleActivate}
      style={isStack ? stackItemStyle : inlineLinkStyle}
    >
      BaseScan
    </a>
  );

  const ethPrice =
    !priceError && fetchedAt != null ? (
      <span style={isStack ? { ...stackItemStyle, cursor: 'default', opacity: 0.92 } : undefined}>
        ETH/USD as of {formatEthPriceTimestamp(fetchedAt)}
      </span>
    ) : null;

  const themeSelector = (
    <div
      style={
        isStack
          ? {
              ...stackItemStyle,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexWrap: 'wrap',
              cursor: 'default',
            }
          : undefined
      }
      onClick={isStack ? (e) => e.stopPropagation() : undefined}
    >
      <ThemeSelector
        theme={theme}
        designMode={designMode}
        setDesignMode={setDesignMode}
        hoverMode={hoverMode}
        setHoverMode={setHoverMode}
        modes={themeModes}
        layout={isStack ? 'stack' : 'inline'}
      />
    </div>
  );

  if (isStack) {
    return (
      <div
        style={{
          marginTop: '0.5rem',
          paddingTop: '0.5rem',
          borderTop: `1px solid ${theme.border}`,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.125rem',
        }}
      >
        {copyright}
        {documents}
        {legal}
        {baseScan}
        {ethPrice}
        {themeSelector}
      </div>
    );
  }

  const dot = <span style={{ opacity: 0.4 }}>·</span>;

  return (
    <>
      {copyright}
      {dot}
      {documents}
      {dot}
      {legal}
      {dot}
      {baseScan}
      {ethPrice && (
        <>
          {dot}
          {ethPrice}
        </>
      )}
      {dot}
      {themeSelector}
    </>
  );
}
